import React, { useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';

type CreativeButtonProps = {
  as?: 'button' | 'a';
  href?: string;
  type?: 'button' | 'submit';
  onClick?: (e: React.MouseEvent) => void;
  onMouseEnter?: (e: React.MouseEvent<HTMLElement>) => void;
  onMouseLeave?: (e: React.MouseEvent<HTMLElement>) => void;
  target?: string;
  rel?: string;
  fillColor: string;
  hoverTextClassName: string;
  className?: string;
  children: React.ReactNode;
  disabled?: boolean;
};

// A pill button whose fill color sweeps in from wherever the cursor enters,
// rather than a flat color/opacity fade — https://blog.olivierlarose.com/demos/creative-buttons
const CreativeButton: React.FC<CreativeButtonProps> = ({
  as = 'button',
  href,
  type = 'button',
  onClick,
  onMouseEnter,
  onMouseLeave,
  target,
  rel,
  fillColor,
  hoverTextClassName,
  className = '',
  children,
  disabled,
}) => {
  const [hovered, setHovered] = useState(false);
  const [origin, setOrigin] = useState({ x: '50%', y: '50%' });
  const prefersReducedMotion = useReducedMotion();

  const updateOrigin = (e: React.MouseEvent<HTMLElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setOrigin({ x: `${e.clientX - rect.left}px`, y: `${e.clientY - rect.top}px` });
  };

  const handleEnter = (e: React.MouseEvent<HTMLElement>) => {
    updateOrigin(e);
    setHovered(true);
    onMouseEnter?.(e);
  };

  const handleLeave = (e: React.MouseEvent<HTMLElement>) => {
    updateOrigin(e);
    setHovered(false);
    onMouseLeave?.(e);
  };

  const sharedProps = {
    onMouseEnter: handleEnter,
    onMouseLeave: handleLeave,
    className: `group relative isolate overflow-hidden inline-flex items-center justify-center gap-2 rounded-full ${className}`,
  };

  const fill = (
    <motion.span
      aria-hidden="true"
      className="absolute rounded-full pointer-events-none"
      style={{
        backgroundColor: fillColor,
        left: origin.x,
        top: origin.y,
        translateX: '-50%',
        translateY: '-50%',
      }}
      initial={false}
      animate={{ width: hovered ? 420 : 0, height: hovered ? 420 : 0 }}
      transition={prefersReducedMotion ? { duration: 0 } : { duration: 0.55, ease: [0.65, 0, 0.35, 1] }}
    />
  );

  const label = (
    <span className={`relative z-10 flex items-center gap-2 transition-colors duration-300 ${hovered ? hoverTextClassName : ''}`}>
      {children}
    </span>
  );

  if (as === 'a') {
    return (
      <a href={href} target={target} rel={rel} {...sharedProps}>
        {fill}
        {label}
      </a>
    );
  }

  return (
    <button type={type} onClick={onClick} disabled={disabled} {...sharedProps}>
      {fill}
      {label}
    </button>
  );
};

export default CreativeButton;
